import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  Sparkles, Loader2, FileText, ClipboardCheck, GitCompare, Wrench,
  BarChart3, GraduationCap, TrendingUp, AlertTriangle, CalendarClock,
  ArrowRight, Clock, FolderOpen, Activity, Download, FileSearch,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { PortalShell } from "../components/layout/PortalShell";
import { useAuth } from "../context/AuthContext";
import {
  apiAuditPrep, apiListAuditPreps, apiGapAnalysis, apiListGapAnalyses,
  apiDocGen, apiListDocGen, apiCapa, apiListCapa,
  apiRiskScoring, apiListRiskScoring, apiTraining, apiListTraining,
  apiSafetyAnalyzer, apiListSafetyAnalyses,
  apiBillingMe, formatApiError,
} from "../lib/api";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const ORG_TYPES = ["Airline", "Charter Operator", "Private Aviation", "Airport Service Organization", "MRO / Maintenance", "Aviation Startup", "Training Organization", "Drone / UAS Operator"];
const FRAMEWORKS = ["FAA Part 5 SMS", "ICAO Annex 19", "EASA", "IS-BAO", "IOSA", "ISAGO", "National CAA"];
const AUDIT_TYPES = ["Initial certification", "Renewal / Recurrent", "Internal audit", "Regulatory inspection", "Customer / Third-party audit"];
const DOC_TYPES = ["SMS Manual", "Operations Manual (SOP)", "Security Program", "Safety Policy", "Emergency Response Plan", "Quality Manual", "Training Manual"];

const MODULE_META = {
  checklist: {
    title: "Audit Checklist Builder",
    Icon: ClipboardCheck,
    desc: "Generate a tailored, audit-ready preparation report for your organization.",
    requiresType: true, typeLabel: "Audit type *", typeOptions: AUDIT_TYPES, typePlaceholder: "Select audit type",
    focusLabel: "Focus areas (optional)", focusPlaceholder: "e.g. SMS, hazard reporting",
    stateLabel: "Current state / known gaps *", statePlaceholder: "Describe your current safety/compliance systems and any known gaps.",
    buttonText: "Generate audit report", successText: "Audit report generated.", defaultType: null,
    run: apiAuditPrep, list: apiListAuditPreps, historyLabel: "Audit report",
  },
  gap: {
    title: "Gap Analysis Engine",
    Icon: GitCompare,
    desc: "Compare your current practices against a regulatory framework and surface prioritized gaps.",
    requiresType: false,
    focusLabel: "Scope / focus area (optional)", focusPlaceholder: "e.g. SMS, security, records",
    stateLabel: "Current practices / state *", statePlaceholder: "Describe your current safety/compliance systems and any known gaps.",
    buttonText: "Generate gap analysis", successText: "Gap analysis generated.", defaultType: "Gap analysis",
    run: apiGapAnalysis, list: apiListGapAnalyses, historyLabel: "Gap analysis",
  },
  capa: {
    title: "Corrective Action Builder",
    Icon: Wrench,
    desc: "Turn audit findings and non-conformities into a prioritized corrective action plan.",
    requiresType: false,
    focusLabel: "Priority context (optional)", focusPlaceholder: "e.g. address High severity first",
    stateLabel: "Findings / non-conformities to address *", statePlaceholder: "Paste the audit findings, non-conformities or gaps you need corrective actions for.",
    buttonText: "Generate action plan", successText: "Corrective action plan generated.", defaultType: "Corrective action plan",
    run: apiCapa, list: apiListCapa, historyLabel: "Action plan",
  },
  docs: {
    title: "Compliance Document Generator",
    Icon: FileText,
    desc: "Draft a professional, ready-to-use aviation manual, SOP or policy in minutes.",
    requiresType: true, typeLabel: "Document type *", typeOptions: DOC_TYPES, typePlaceholder: "Select document",
    focusLabel: "Sections to focus on (optional)", focusPlaceholder: "e.g. hazard reporting, SPIs, roles",
    stateLabel: "Organization context / specifics *", statePlaceholder: "Describe your organization, operations and anything the document must reflect.",
    buttonText: "Generate document", successText: "Document generated.", defaultType: null,
    run: apiDocGen, list: apiListDocGen, historyLabel: "Document",
  },
  risk: {
    title: "Risk Scoring & SPI Monitor",
    Icon: BarChart3,
    desc: "Score organizational safety risk and define the performance indicators to monitor it.",
    requiresType: false,
    focusLabel: "Operations / hazards of concern (optional)", focusPlaceholder: "e.g. runway excursions, fatigue, ground ops",
    stateLabel: "Safety data, hazards & recent events *", statePlaceholder: "Describe your hazard landscape, incident history, existing controls and any safety data you track.",
    buttonText: "Generate risk assessment", successText: "Risk assessment generated.", defaultType: "Risk scoring & SPI assessment",
    run: apiRiskScoring, list: apiListRiskScoring, historyLabel: "Risk assessment",
  },
  training: {
    title: "Training & Competency AI",
    Icon: GraduationCap,
    desc: "Build role-based training programs, competency matrices and recurrent schedules.",
    requiresType: false,
    focusLabel: "Roles / departments in scope (optional)", focusPlaceholder: "e.g. flight crew, dispatch, maintenance",
    stateLabel: "Current training status & competency gaps *", statePlaceholder: "Describe roles, current training status, expiring qualifications and known competency gaps.",
    buttonText: "Generate training program", successText: "Training program generated.", defaultType: "Training & competency program",
    run: apiTraining, list: apiListTraining, historyLabel: "Training plan",
  },
  analyzer: {
    title: "Safety Report Analyzer",
    Icon: FileSearch,
    desc: "Paste a safety report — AI classifies hazards, rates risk and recommends actions.",
    requiresType: false,
    focusLabel: "Context / areas of concern (optional)", focusPlaceholder: "e.g. ground ops, fatigue events",
    stateLabel: "Safety report text *", statePlaceholder: "Paste the safety report, occurrence summary or hazard log you want analyzed.",
    buttonText: "Analyze report", successText: "Safety report analyzed.", defaultType: "Safety report analysis",
    run: apiSafetyAnalyzer, list: apiListSafetyAnalyses, historyLabel: "Safety analysis",
  },
};

const MODULES = [
  { key: "checklist", active: true, gridDesc: "Generate tailored, audit-ready preparation checklists and readiness reports." },
  { key: "gap", active: true, gridDesc: "Compare your systems against regulatory requirements and surface gaps." },
  { key: "capa", active: true, gridDesc: "Turn findings into prioritized corrective action plans automatically." },
  { key: "docs", active: true, gridDesc: "Draft manuals, SOPs and compliance documentation in minutes." },
  { key: "risk", active: true, gridDesc: "Score risk and monitor safety performance indicators over time." },
  { key: "training", active: true, gridDesc: "Detect competency gaps and build role-based training programs." },
  { key: "analyzer", active: true, gridDesc: "Turn safety reports into classified hazards and prioritized actions." },
];

const STANDARDS = [
  { code: "ICAO", label: "Annex 19" },
  { code: "FAA", label: "Part 5 SMS" },
  { code: "EASA", label: "European" },
  { code: "IS-BAO", label: "Business Aviation" },
  { code: "IOSA", label: "Airline Ops" },
  { code: "CAA", label: "National" },
];

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="relative overflow-hidden rounded-2xl border border-border bg-white p-6">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <div className="mt-3 font-display text-3xl font-extrabold text-navy">{value}</div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </div>
      <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </div>
);

const EMPTY_FORM = { organization_name: "", organization_type: "", regulatory_framework: "", audit_type: "", current_state: "", focus_areas: "" };

export default function Dashboard() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeModule, setActiveModule] = useState("checklist");
  const [usage, setUsage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const reportRef = useRef(null);

  const meta = MODULE_META[activeModule];

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const loadHistory = async () => {
    try {
      const kinds = Object.keys(MODULE_META);
      const results = await Promise.all(kinds.map((k) => MODULE_META[k].list().catch(() => [])));
      const merged = results
        .flatMap((arr, i) => arr.map((x) => ({ ...x, kind: kinds[i] })))
        .sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
      setHistory(merged);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); apiBillingMe().then(setUsage).catch(() => {}); }, []);

  const stats = useMemo(() => {
    const docs = history.length;
    return {
      score: docs ? "82%" : "—",
      findings: docs ? docs * 3 : 0,
      docs,
      audits: docs ? 1 : 0,
    };
  }, [history]);

  const riskTrend = useMemo(() => history
    .filter((h) => h.kind === "risk" && typeof h.risk_score === "number")
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((h) => ({
      date: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      score: h.risk_score,
      org: h.input?.organization_name || "",
    })), [history]);

  const latestRisk = riskTrend[riskTrend.length - 1];
  const prevRisk = riskTrend.length > 1 ? riskTrend[riskTrend.length - 2] : null;
  const riskDelta = latestRisk && prevRisk ? latestRisk.score - prevRisk.score : null;
  const riskBand = !latestRisk ? null
    : latestRisk.score >= 75 ? { label: "Critical", cls: "bg-red-100 text-red-700" }
    : latestRisk.score >= 50 ? { label: "High", cls: "bg-amber-100 text-amber-700" }
    : latestRisk.score >= 25 ? { label: "Moderate", cls: "bg-yellow-100 text-yellow-700" }
    : { label: "Low", cls: "bg-emerald-100 text-emerald-700" };

  const openModule = (m) => {
    if (!m.active) { toast.info(`${MODULE_META[m.key].title} is coming soon.`); return; }
    setActiveModule(m.key);
    setReport(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const onGenerate = async (e) => {
    e.preventDefault();
    if (!form.organization_name || !form.organization_type || !form.regulatory_framework || !form.current_state || (meta.requiresType && !form.audit_type)) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const payload = { ...form, audit_type: meta.defaultType || form.audit_type };
      const res = await meta.run(payload);
      setReport(res);
      toast.success(meta.successText);
      loadHistory();
      apiBillingMe().then(setUsage).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    const html = reportRef.current?.innerHTML;
    if (!html) return;
    const org = report?.input?.organization_name || "AuditGuard";
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to download the PDF."); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${meta.title} — ${org}</title>
      <style>
        @page { margin: 22mm 18mm; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; color:#111827; line-height:1.55; }
        .brand { border-bottom:3px solid #F97316; padding-bottom:10px; margin-bottom:24px; }
        .brand h1 { font-size:18px; color:#1E3A8A; margin:0; }
        .brand p { font-size:11px; color:#6B7280; margin:4px 0 0; }
        h1,h2,h3 { color:#0B1120; } h2 { border-bottom:1px solid #E5E7EB; padding-bottom:4px; margin-top:26px; }
        table { border-collapse:collapse; width:100%; font-size:12px; }
        th,td { border:1px solid #E5E7EB; padding:6px 8px; text-align:left; }
        th { background:#F3F4F6; }
        .footer { margin-top:30px; border-top:1px solid #E5E7EB; padding-top:10px; font-size:10px; color:#9CA3AF; }
      </style></head><body>
      <div class="brand"><h1>Global-Elect Aviation Services · AuditGuard AI</h1><p>${meta.title} — ${org} · Generated ${new Date().toLocaleDateString()}</p></div>
      ${html}
      <div class="footer">Generated by AuditGuard AI — a division of Global-Elect Management Company (G-EMC). This document is AI-generated and should be reviewed by a qualified professional.</div>
      </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  return (
    <PortalShell>
      {/* Hero */}
      <div className="mb-8 flex flex-col justify-between gap-4 rounded-2xl border border-border bg-gradient-to-r from-navy to-steel p-6 text-white sm:flex-row sm:items-center lg:p-8">
        <div className="flex items-center gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
            <Sparkles className="h-6 w-6 text-aero" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight lg:text-3xl">AuditGuard AI Dashboard</h1>
            <p className="mt-1 text-sm text-white/60">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""} · Aviation compliance automation</p>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          {usage && (
            <Link to="/billing" data-testid="usage-badge" className="rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-center text-xs text-white/80 transition-colors hover:bg-white/10">
              <span className="font-semibold text-white">{usage.plan_name}</span> · {usage.used}/{usage.limit} AI used
              <span className="mt-0.5 block text-[11px] text-aero">Manage plan →</span>
            </Link>
          )}
          <button onClick={() => openModule(MODULES[0])} data-testid="hero-new-report" className="inline-flex items-center justify-center gap-2 rounded-full bg-aero px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
            <Sparkles className="h-4 w-4" /> New audit report
          </button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={TrendingUp} label="Compliance Score" value={stats.score} sub="Indicative readiness" accent="bg-brand/10 text-brand" />
        <StatCard icon={AlertTriangle} label="Open Findings" value={stats.findings} sub="Across active reports" accent="bg-amber-100 text-amber-600" />
        <StatCard icon={FileText} label="Documents Generated" value={stats.docs} sub="AI reports created" accent="bg-aero/10 text-aero" />
        <StatCard icon={CalendarClock} label="Upcoming Audits" value={stats.audits} sub="Next 90 days" accent="bg-emerald-100 text-emerald-600" />
      </div>

      {/* Risk score trend */}
      {riskTrend.length > 0 && (
        <div className="mt-10" data-testid="risk-trend-widget">
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-brand" />
                <h2 className="font-display text-lg font-bold text-navy">Risk Score Trend</h2>
              </div>
              <div className="flex items-center gap-3">
                {riskDelta !== null && (
                  <span data-testid="risk-trend-delta" className={`text-xs font-semibold ${riskDelta > 0 ? "text-red-600" : riskDelta < 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {riskDelta > 0 ? `▲ +${riskDelta}` : riskDelta < 0 ? `▼ ${riskDelta}` : "— 0"} vs previous
                  </span>
                )}
                <span data-testid="risk-trend-latest" className={`rounded-full px-3 py-1 text-xs font-bold ${riskBand.cls}`}>
                  {latestRisk.score}/100 · {riskBand.label}
                </span>
              </div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={riskTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="riskFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F97316" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#F97316" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    formatter={(v) => [`${v}/100`, "Risk score"]}
                    labelFormatter={(l, p) => (p?.[0]?.payload?.org ? `${l} — ${p[0].payload.org}` : l)}
                  />
                  <ReferenceLine y={75} stroke="#DC2626" strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="score" stroke="#F97316" strokeWidth={2.5} fill="url(#riskFill)" dot={{ r: 4, fill: "#F97316" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Overall organizational risk score (0–100) from your saved risk assessments. Lower is better.</p>
          </div>
        </div>
      )}

      {/* Modules grid */}
      <div className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-navy">Audit Modules</h2>
          <span className="text-xs text-muted-foreground">{MODULES.filter((m) => m.active).length} active · {MODULES.filter((m) => !m.active).length} coming soon</span>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => {
            const MIcon = MODULE_META[m.key].Icon;
            return (
              <div key={m.key} data-testid={`module-${m.key}`} className={`group flex flex-col rounded-2xl border bg-white p-6 transition-transform hover:-translate-y-1 hover:shadow-lg ${m.active ? "border-brand/30" : "border-border"}`}>
                <div className="flex items-start justify-between">
                  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${m.active ? "bg-brand/10 text-brand" : "bg-secondary text-steel"}`}>
                    <MIcon className="h-6 w-6" />
                  </span>
                  {m.active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Active</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-aero/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-aero"><Clock className="h-3 w-3" /> Soon</span>
                  )}
                </div>
                <h3 className="mt-5 font-display text-base font-bold text-navy">{MODULE_META[m.key].title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{m.gridDesc}</p>
                <button
                  onClick={() => openModule(m)}
                  data-testid={`open-module-${m.key}`}
                  disabled={!m.active}
                  className={`mt-5 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform ${m.active ? "bg-navy text-white hover:-translate-y-0.5" : "cursor-not-allowed bg-secondary text-muted-foreground"}`}
                >
                  {m.active ? "Open Module" : "Coming Soon"} {m.active && <ArrowRight className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Compliance standards */}
      <div className="mt-10">
        <h2 className="mb-4 font-display text-lg font-bold text-navy">Compliance Standards</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {STANDARDS.map((s) => (
            <div key={s.code} className="flex flex-col items-center justify-center rounded-2xl border border-border bg-white p-5 text-center transition-colors hover:border-brand/40">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 font-display text-xs font-extrabold text-brand">{s.code}</span>
              <div className="mt-3 text-sm font-semibold text-navy">{s.code}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity + Library */}
      <div className="mt-10 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-border bg-white p-6">
            <div className="mb-4 flex items-center gap-2">
              <Activity className="h-5 w-5 text-brand" />
              <h2 className="font-display text-lg font-bold text-navy">Recent Activity</h2>
            </div>
            {history.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No activity yet. Generate your first audit report to get started.</p>
            ) : (
              <ul className="divide-y divide-border">
                {history.slice(0, 6).map((h) => {
                  const HIcon = MODULE_META[h.kind]?.Icon || ClipboardCheck;
                  return (
                    <li key={h.id} className="flex items-center gap-3 py-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-aero/10 text-aero"><HIcon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-navy">{MODULE_META[h.kind]?.historyLabel || "Report"} · {h.input?.organization_name}</div>
                        <div className="text-xs text-muted-foreground">{h.input?.regulatory_framework} · {h.input?.audit_type}</div>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{new Date(h.created_at).toLocaleDateString()}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="flex h-full flex-col rounded-2xl border border-border bg-white p-6">
            <div className="mb-4 flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-brand" />
              <h2 className="font-display text-lg font-bold text-navy">Document Library</h2>
            </div>
            {history.length === 0 ? (
              <p className="flex-1 py-8 text-center text-sm text-muted-foreground">Your generated documents will appear here.</p>
            ) : (
              <ul className="flex-1 space-y-3">
                {history.slice(0, 4).map((h) => (
                  <li key={h.id}>
                    <button onClick={() => { setActiveModule(h.kind || "checklist"); setReport(h); setOpen(true); }} data-testid={`library-${h.id}`} className="flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:bg-secondary/60">
                      <FileText className="h-4 w-4 shrink-0 text-brand" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-navy">{h.input?.organization_name} — {h.input?.audit_type}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => openModule(MODULES[0])} className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-secondary">
              Open Library <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <footer className="mt-12 border-t border-border pt-6 text-center text-sm text-muted-foreground">
        Global-Elect Aviation Services — Powered by AuditGuard AI
      </footer>

      {/* Module dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <meta.Icon className="h-5 w-5 text-brand" /> {meta.title}
            </DialogTitle>
            <DialogDescription>{meta.desc}</DialogDescription>
          </DialogHeader>

          {report ? (
            <div>
              <article ref={reportRef} className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-navy prose-strong:text-navy prose-a:text-brand prose-table:text-sm">
                <ReactMarkdown>{report.report}</ReactMarkdown>
              </article>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={downloadPdf} data-testid="download-pdf" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">
                  <Download className="h-4 w-4" /> Download PDF
                </button>
                <button onClick={() => setReport(null)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-navy hover:bg-secondary">
                  <ArrowRight className="h-4 w-4 rotate-180" /> New
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
              <p className="mt-4 text-sm text-muted-foreground">AuditGuard AI is analyzing your organization…</p>
            </div>
          ) : (
            <form onSubmit={onGenerate} data-testid="audit-form" className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Organization name *</Label>
                <Input data-testid="audit-org-name" value={form.organization_name} onChange={update("organization_name")} className="mt-2" placeholder="e.g. SkyLink Charter" />
              </div>
              <div>
                <Label>Organization type *</Label>
                <Select value={form.organization_type} onValueChange={setField("organization_type")}>
                  <SelectTrigger data-testid="audit-org-type" className="mt-2"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Regulatory framework *</Label>
                <Select value={form.regulatory_framework} onValueChange={setField("regulatory_framework")}>
                  <SelectTrigger data-testid="audit-framework" className="mt-2"><SelectValue placeholder="Select framework" /></SelectTrigger>
                  <SelectContent>{FRAMEWORKS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {meta.requiresType && (
                <div>
                  <Label>{meta.typeLabel}</Label>
                  <Select value={form.audit_type} onValueChange={setField("audit_type")}>
                    <SelectTrigger data-testid="audit-type" className="mt-2"><SelectValue placeholder={meta.typePlaceholder} /></SelectTrigger>
                    <SelectContent>{meta.typeOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>{meta.focusLabel}</Label>
                <Input data-testid="audit-focus" value={form.focus_areas} onChange={update("focus_areas")} className="mt-2" placeholder={meta.focusPlaceholder} />
              </div>
              <div className="sm:col-span-2">
                <Label>{meta.stateLabel}</Label>
                <Textarea data-testid="audit-current-state" value={form.current_state} onChange={update("current_state")} className="mt-2 min-h-[100px]" placeholder={meta.statePlaceholder} />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="audit-generate" className="flex w-full items-center justify-center gap-2 rounded-lg bg-aero px-4 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  <Sparkles className="h-4 w-4" /> {meta.buttonText}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </PortalShell>
  );
}
